import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  LoadingSkeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  toast,
} from '@berky/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'

import { findModule } from '../modules'
import { DIREKT, usePlatformApi, type RollenTraeger, type VerwaltungsRolle } from './api'

/**
 * Der Rollenkatalog: was es gibt, wer es trägt, und was niemand mehr anmeldet.
 *
 * Nach Modul gruppiert, weil ein Rollenname ohne sein Modul nichts sagt — und
 * weil so auf einen Blick sichtbar wird, wenn ein Modul verschwunden ist und
 * seine Rollen liegengeblieben sind.
 */
export function RollenTab() {
  const api = usePlatformApi()
  const queryClient = useQueryClient()

  const [zuLoeschen, setZuLoeschen] = useState<string | null>(null)
  const [suche, setSuche] = useState('')
  const [modulFilter, setModulFilter] = useState('alle')
  const [statusFilter, setStatusFilter] = useState<'alle' | 'verwaist'>('alle')

  const rollen = useQuery({
    queryKey: ['verwaltung', 'roles'],
    queryFn: ({ signal }) => api.get<VerwaltungsRolle[]>('/roles', { signal }),
  })

  const verwaist = useQuery({
    queryKey: ['verwaltung', 'rollen', 'verwaist'],
    queryFn: ({ signal }) => api.get<string[]>('/rollen/verwaist', { signal }),
  })

  const loeschen = useMutation({
    mutationFn: (rolle: string) => api.delete(`/rollen/${encodeURIComponent(rolle)}`),
    onSuccess: async () => {
      toast.success('Rolle gelöscht')
      setZuLoeschen(null)
      await queryClient.invalidateQueries({ queryKey: ['verwaltung'] })
    },
  })

  if (rollen.isPending) {
    return <LoadingSkeleton variant="text" rows={6} />
  }

  if (rollen.isError || verwaist.isError) {
    return (
      <ErrorState
        description="Der Rollenkatalog konnte nicht vollständig geladen werden."
        onRetry={() => {
          void rollen.refetch()
          void verwaist.refetch()
        }}
      />
    )
  }

  const katalog = rollen.data ?? []

  if (katalog.length === 0) {
    return (
      <EmptyState
        title="Keine Rollen angemeldet"
        description="Rollen bringt jedes Modul beim Start selbst mit."
      />
    )
  }

  const verwaisteNamen = new Set(verwaist.data ?? [])
  const module = [...new Set(katalog.map((rolle) => rolle.moduleId))].sort((a, b) =>
    a.localeCompare(b, 'de'),
  )
  const gefilterterKatalog = katalog.filter((rolle) => {
    const begriff = suche.trim().toLocaleLowerCase('de')
    const passtZurSuche =
      !begriff ||
      [rolle.name, rolle.description].some((wert) =>
        wert.toLocaleLowerCase('de').includes(begriff),
      )
    const passtZumModul = modulFilter === 'alle' || rolle.moduleId === modulFilter
    const passtZumStatus = statusFilter === 'alle' || verwaisteNamen.has(rolle.name)
    return passtZurSuche && passtZumModul && passtZumStatus
  })
  const gefilterteModule = module.filter((modulId) =>
    gefilterterKatalog.some((rolle) => rolle.moduleId === modulId),
  )

  return (
    <div className="space-y-8 pt-4">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-muted/30 p-4">
        <div className="w-full min-w-0 sm:min-w-64 sm:flex-1">
          <p className="mb-1 text-sm font-medium">Rollenkatalog durchsuchen</p>
          <Input
            value={suche}
            onChange={(event) => setSuche(event.target.value)}
            placeholder="Rolle oder Beschreibung suchen"
            aria-label="Rollenkatalog durchsuchen"
          />
        </div>
        <Select value={modulFilter} onValueChange={setModulFilter}>
          <SelectTrigger aria-label="Nach Modul filtern" className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Module</SelectItem>
            {module.map((modulId) => (
              <SelectItem key={modulId} value={modulId}>
                {findModule(modulId)?.title ??
                  (modulId === 'platform' ? 'Plattform' : modulId)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as 'alle' | 'verwaist')}
        >
          <SelectTrigger aria-label="Nach Rollenstatus filtern" className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Rollen</SelectItem>
            <SelectItem value="verwaist">Nur verwaiste Rollen</SelectItem>
          </SelectContent>
        </Select>
        <p className="w-full text-sm text-muted-foreground">
          {gefilterterKatalog.length} von {katalog.length} Rollen angezeigt
        </p>
      </div>

      {gefilterteModule.map((modulId) => (
        <section key={modulId} className="space-y-2">
          <h2
            aria-label={modulId}
            className="text-sm font-medium tracking-wide text-muted-foreground uppercase"
          >
            {findModule(modulId)?.title ??
              (modulId === 'platform' ? 'Plattform' : modulId)}
          </h2>

          <ul className="grid gap-3 md:grid-cols-2">
            {gefilterterKatalog
              .filter((rolle) => rolle.moduleId === modulId)
              .map((rolle) => (
                <RollenZeile
                  key={rolle.name}
                  rolle={rolle}
                  verwaist={verwaisteNamen.has(rolle.name)}
                  onLoeschen={() => setZuLoeschen(rolle.name)}
                />
              ))}
          </ul>
        </section>
      ))}

      <ConfirmDialog
        open={zuLoeschen !== null}
        onOpenChange={(offen) => !offen && setZuLoeschen(null)}
        title="Rolle löschen?"
        description={
          zuLoeschen
            ? `Die Rolle ${zuLoeschen} wird mitsamt ihren Zuweisungen und Gruppenverknüpfungen entfernt. Das lässt sich nicht rückgängig machen.`
            : ''
        }
        confirmLabel="Löschen"
        destructive
        pending={loeschen.isPending}
        onConfirm={() => zuLoeschen && loeschen.mutate(zuLoeschen)}
      />
    </div>
  )
}

/** Eine Rolle mit Zuweisungszahlen und aufklappbarer Herkunftsliste. */
function RollenZeile({
  rolle,
  verwaist,
  onLoeschen,
}: {
  rolle: VerwaltungsRolle
  verwaist: boolean
  onLoeschen: () => void
}) {
  const api = usePlatformApi()
  const [offen, setOffen] = useState(false)

  const traeger = useQuery({
    queryKey: ['verwaltung', 'traeger', rolle.name],
    queryFn: ({ signal }) =>
      api.get<RollenTraeger[]>(`/rollen/${encodeURIComponent(rolle.name)}/traeger`, {
        signal,
      }),
    // Die Zahlen gehören zur Kartenübersicht. Die Namen der Träger werden
    // trotzdem erst beim Aufklappen gerendert, damit die Oberfläche ruhig bleibt.
    enabled: true,
  })

  return (
    <li className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={offen}
          onClick={() => setOffen((vorher) => !vorher)}
        >
          {offen ? (
            <ChevronDown className="size-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="size-4" aria-hidden="true" />
          )}
          {rolle.name}
        </Button>

        {verwaist && <StatusBadge tone="warning">verwaist</StatusBadge>}

        {!traeger.isPending && traeger.data && (
          <StatusBadge tone="neutral">{traeger.data.length} Zuweisungen</StatusBadge>
        )}

        {verwaist && (
          <Button variant="ghost" size="sm" onClick={onLoeschen}>
            Rolle löschen
          </Button>
        )}
      </div>

      <p className="mt-2 text-sm text-muted-foreground">{rolle.description}</p>

      {offen && (
        <div className="mt-3 border-t pt-3">
          {traeger.isPending ? (
            <LoadingSkeleton variant="text" rows={2} />
          ) : traeger.data && traeger.data.length > 0 ? (
            <>
              <p className="mb-2 text-sm text-muted-foreground">
                {traeger.data.filter((eintrag) => eintrag.source === DIREKT).length}{' '}
                direkt ·{' '}
                {traeger.data.filter((eintrag) => eintrag.source !== DIREKT).length} über
                Gruppen
              </p>
              <ul className="space-y-1">
                {traeger.data.map((eintrag) => (
                  <li
                    key={`${eintrag.userId ?? 'gruppe'}:${eintrag.name}:${eintrag.source}`}
                    className="flex flex-wrap items-center gap-2 text-sm"
                  >
                    <span>{eintrag.name}</span>
                    <StatusBadge tone={eintrag.source === DIREKT ? 'info' : 'neutral'}>
                      {eintrag.source === DIREKT ? 'direkt' : `über ${eintrag.source}`}
                    </StatusBadge>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Diese Rolle trägt derzeit niemand.
            </p>
          )}
        </div>
      )}
    </li>
  )
}
